// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  ProgressBar,
  Button,
  Stack,
  Tag,
  InlineNotification,
  Loading,
  UnorderedList,
} from '@carbon/react';
import { Education, Trophy, User, Dashboard, Calendar, UserMultiple } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/authService';

// Import chart components
import LearningProgressChart from '../../components/charts/LearningProgressChart';
import EngagementBarChart from '../../components/charts/EngagementBarChart';
import SubjectRadarChart from '../../components/charts/SubjectRadarChart';
import WeeklyTrendChart from '../../components/charts/WeeklyTrendChart';

// Typography components handled differently in Carbon
// Note: Using Carbon Design System instead of Ant Design theme tokens

// Enhanced interfaces
interface FamilyDashboard {
  family_summary: {
    family_id: string;
    family_name: string;
    children_count: number;
    total_learning_hours: number;
    family_engagement_score: number;
    weekly_improvement: number;
    monthly_improvement: number;
  };
  children_progress: Array<{
    student_id: string;
    student_name: string;
    grade_level: number;
    age_group: string;
    avatar_url?: string;
    recent_activity_summary: {
      total_sessions: number;
      total_minutes: number;
      favorite_subject: string;
      last_session: string | null;
      week_over_week_change: number;
    };
    progress_highlights: string[];
    areas_of_concern: Array<{
      area: string;
      severity: string;
      recommended_action: string;
    }>;
    parent_action_items: string[];
    mastery_level: number;
    engagement_score: number;
    current_streak: number;
  }>;
  family_communication_center: {
    unread_notifications: number;
    recent_achievements_to_celebrate: Array<{
      student_id: string;
      student_name: string;
      milestone_description: string;
      achieved_date: string;
      significance_level: string;
      icon?: string;
    }>;
    system_updates: Array<{
      type: string;
      message: string;
      date: string;
    }>;
  };
  collaborative_learning_opportunities: Array<{
    activity_title: string;
    estimated_duration: string;
    educational_value: string;
    materials_needed: string[];
    age_appropriateness: string;
    difficulty_level: string;
  }>;
  learning_insights: {
    peak_learning_time: string;
    average_session_duration: number;
    strongest_subjects: string[];
    improvement_areas: string[];
    recommended_focus: string;
  };
}

// Mock chart data generators
const generateProgressData = () => {
  const dates = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push({
      date: date.toISOString().split('T')[0],
      mastery: Math.floor(60 + Math.random() * 30 + (30 - i) * 0.5),
      engagement: Math.floor(30 + Math.random() * 60),
      student: 'All Students',
    });
  }
  return dates;
};

const generateEngagementData = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const categories = ['Active Learning', 'Practice', 'Review'];
  const data: any[] = [];

  days.forEach(day => {
    categories.forEach(category => {
      data.push({
        day,
        sessions: Math.floor(1 + Math.random() * 5),
        minutes: Math.floor(15 + Math.random() * 45),
        category,
      });
    });
  });

  return data;
};

const generateSubjectData = () => {
  const subjects = ['Math', 'Science', 'Reading', 'Writing', 'History', 'Art'];
  return subjects.map(subject => ({
    subject,
    score: Math.floor(65 + Math.random() * 35),
    potential: Math.floor(80 + Math.random() * 20),
    student: 'Current Performance',
  }));
};

const generateWeeklyData = () => {
  const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  return weeks.map((week, index) => ({
    week,
    learningHours: 5 + Math.random() * 10 + index * 2,
    completionRate: Math.floor(70 + Math.random() * 25),
    category: 'Learning',
  }));
};

function ParentDashboardEnhanced() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [dashboardData, setDashboardData] = useState<FamilyDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState('overview');

  // Chart data states
  const [progressData] = useState(generateProgressData());
  const [engagementData] = useState(generateEngagementData());
  const [subjectData] = useState(generateSubjectData());
  const [weeklyData] = useState(generateWeeklyData());

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/families/${user?.family_id}/dashboard`);

        // Transform API response to match expected structure
        const apiData = response.data.data;
        const baseData: FamilyDashboard = {
          family_summary: {
            family_id: apiData.familyOverview?.familyId || user?.family_id || '',
            family_name: apiData.familyOverview?.familyName || user?.family_name || 'Your Family',
            children_count: apiData.familyOverview?.memberCount || 0,
            total_learning_hours: Math.round((apiData.engagementOverview?.totalMinutes || 0) / 60),
            family_engagement_score:
              apiData.engagementOverview?.activeChildren > 0
                ? Math.min(
                    10,
                    Math.round(
                      (apiData.engagementOverview?.totalSessions || 0) /
                        (apiData.engagementOverview?.activeChildren || 1)
                    )
                  )
                : 5,
            weekly_improvement: 12,
            monthly_improvement: 28,
          },
          children_progress: (apiData.childrenSummary || []).map((child: any) => ({
            student_id: child.studentId || '',
            student_name: `Student Grade ${child.gradeLevel}`,
            grade_level: child.gradeLevel || 0,
            age_group: child.ageGroup || 'elementary',
            mastery_level: Math.floor(70 + Math.random() * 25),
            engagement_score: Math.floor(60 + Math.random() * 35),
            current_streak: Math.floor(3 + Math.random() * 10),
            recent_activity_summary: {
              total_sessions: 0,
              total_minutes: 0,
              favorite_subject: 'Math',
              last_session: child.lastActive || null,
              week_over_week_change: Math.floor(-10 + Math.random() * 30),
            },
            progress_highlights: [],
            areas_of_concern: [],
            parent_action_items: [],
          })),
          family_communication_center: {
            unread_notifications: 0,
            recent_achievements_to_celebrate: [],
            system_updates: [],
          },
          collaborative_learning_opportunities: [
            {
              activity_title: 'Family Math Challenge',
              estimated_duration: '30 minutes',
              educational_value: 'Practice problem-solving together',
              materials_needed: ['Paper', 'Pencil'],
              age_appropriateness: 'All ages',
              difficulty_level: 'Beginner',
            },
          ],
          learning_insights: {
            peak_learning_time: '3:00 PM - 5:00 PM',
            average_session_duration: 35,
            strongest_subjects: ['Mathematics', 'Science'],
            improvement_areas: ['Writing', 'Reading Comprehension'],
            recommended_focus: 'Focus on daily reading practice for 20 minutes',
          },
        };

        setDashboardData(baseData);
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user?.family_id) {
      fetchDashboard();
    }
  }, [user?.family_id]);

  const getEngagementColor = (score: number) => {
    if (score >= 8) return '#52c41a';
    if (score >= 6) return '#1890ff';
    if (score >= 4) return '#faad14';
    return '#f5222d';
  };

  const getTrendIcon = (change: number) => {
    if (change > 0) return <RiseOutlined style={{ color: '#52c41a' }} />;
    if (change < 0) return <FallOutlined style={{ color: '#f5222d' }} />;
    return null;
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Tile style={{ padding: 40, textAlign: 'center' }}>
          <Loading />
          <h4 style={{ marginTop: 20, color: '#595959' }}>Loading Your Family Dashboard...</h4>
        </Tile>
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        title="Dashboard Error"
        subtitle={error}
        kind="error"
        hideCloseButton={false}
        actions={
          <Button size="sm" kind="danger" onClick={() => window.location.reload()}>
            Retry
          </Button>
        }
        style={{ margin: 24 }}
      />
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Tile>
          <h3>No dashboard data available</h3>
          <p>Please complete your setup to view your family dashboard.</p>
          <Button kind="primary" onClick={() => navigate('/setup')}>
            Complete Setup
          </Button>
        </Tile>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '24px',
        background: '#f5f7fa',
        minHeight: '100vh',
      }}
    >
      {/* Enhanced Header with Gradient */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 8,
          padding: '32px',
          marginBottom: 32,
          color: 'white',
        }}
      >
        <Grid align="middle" justify="space-between">
          <Column>
            <Title level={2} style={{ margin: 0, color: 'white' }}>
              <Dashboard /> Family Learning Hub
            </Title>
            <span style={{ fontSize: '18px', color: 'rgba(255,255,0.9)' }}>
              Welcome back! Your family's learning journey continues...
            </span>
          </Column>
          <Column>
            <Stack>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,0.8)' }}>Weekly Goal</span>}
                value={85}
                suffix="%"
                valueStyle={{ color: 'white', fontSize: 28 }}
                prefix={<FireOutlined />}
              />
            </Stack>
          </Column>
        </Grid>
      </div>

      {/* View Selector */}
      <div style={{ marginBottom: 24 }}>
        <Segmented
          options={[
            { label: 'Overview', value: 'overview', icon: <Dashboard /> },
            { label: 'Progress', value: 'progress', icon: <RiseOutlined /> },
            { label: 'Insights', value: 'insights', icon: <BulbOutlined /> },
            { label: 'Activities', value: 'activities', icon: <UserMultiple /> },
          ]}
          value={selectedView}
          onChange={setSelectedView}
          style={{ marginBottom: 16 }}
          size="large"
        />
      </div>

      {/* Enhanced KPI Cards with Trends */}
      <Grid gutter={[16]} style={{ marginBottom: 32 }}>
        <Column xs={24} sm={12} lg={6}>
          <Tile
            hoverable
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,0.9)' }}>Active Learners</span>}
              value={dashboardData.family_summary.children_count}
              valueStyle={{ color: 'white' }}
              prefix={<User style={{ fontSize: 20 }} />}
              suffix={<span style={{ fontSize: 14, color: 'rgba(255,255,0.8)' }}>students</span>}
            />
            <div style={{ marginTop: 8 }}>
              <Tag status="processing" text={<span style={{ color: 'white' }}>All Active</span>} />
            </div>
          </Tile>
        </Column>

        <Column xs={24} sm={12} lg={6}>
          <Tile
            hoverable
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,0.9)' }}>Learning Hours</span>}
              value={dashboardData.family_summary.total_learning_hours}
              valueStyle={{ color: 'white' }}
              prefix={<ClockCircleOutlined style={{ fontSize: 20 }} />}
              suffix={
                <Stack>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,0.8)' }}>hrs</span>
                  {getTrendIcon(dashboardData.family_summary.weekly_improvement)}
                </Stack>
              }
            />
            <ProgressIndicator
              percent={75}
              strokeColor="white"
              trailColor="rgba(255,255,0.3)"
              showInfo={false}
              style={{ marginTop: 8 }}
            />
          </Tile>
        </Column>

        <Column xs={24} sm={12} lg={6}>
          <Tile
            hoverable
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,0.9)' }}>Engagement Score</span>}
              value={dashboardData.family_summary.family_engagement_score}
              valueStyle={{ color: 'white' }}
              prefix={<StarOutlined style={{ fontSize: 20 }} />}
              suffix={<span style={{ fontSize: 14, color: 'rgba(255,255,0.8)' }}>/ 10</span>}
            />
            <div style={{ marginTop: 8 }}>
              <Stack>
                {[...Array(5)].map((_, i) => (
                  <StarOutlined
                    key={i}
                    style={{
                      color:
                        i < Math.floor(dashboardData.family_summary.family_engagement_score / 2)
                          ? 'white'
                          : 'rgba(255,255,0.3)',
                    }}
                  />
                ))}
              </Stack>
            </div>
          </Tile>
        </Column>

        <Column xs={24} sm={12} lg={6}>
          <Tile
            hoverable
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,0.9)' }}>Achievements</span>}
              value={dashboardData.family_communication_center.recent_achievements_to_celebrate.length}
              valueStyle={{ color: 'white' }}
              prefix={<Trophy style={{ fontSize: 20 }} />}
              suffix={<span style={{ fontSize: 14, color: 'rgba(255,255,0.8)' }}>new</span>}
            />
            <div style={{ marginTop: 8 }}>
              <ThunderboltOutlined style={{ color: 'yellow', marginRight: 4 }} />
              <span style={{ color: 'white', fontSize: 12 }}>Celebrate today!</span>
            </div>
          </Tile>
        </Column>
      </Grid>

      {/* Charts Section */}
      <Grid gutter={[16]} style={{ marginBottom: 32 }}>
        <Column xs={24} lg={12}>
          <LearningProgressChart data={progressData} />
        </Column>
        <Column xs={24} lg={12}>
          <EngagementBarChart data={engagementData} />
        </Column>
      </Grid>

      <Grid gutter={[16]} style={{ marginBottom: 32 }}>
        <Column xs={24} lg={12}>
          <SubjectRadarChart data={subjectData} />
        </Column>
        <Column xs={24} lg={12}>
          <WeeklyTrendChart data={weeklyData} />
        </Column>
      </Grid>

      {/* Enhanced Children Progress Cards */}
      <Title level={3} style={{ marginBottom: 16 }}>
        <UserMultiple /> Individual Progress
      </Title>
      <Grid gutter={[16]} style={{ marginBottom: 32 }}>
        {dashboardData.children_progress.map(child => (
          <Column xs={24} lg={12} key={child.student_id}>
            <Tile
              hoverable
              style={{
                borderRadius: 8,
                overflow: 'hidden',
              }}
              title={
                <Stack>
                  {/* Avatar placeholder */}G{child.grade_level}
                  <div>
                    <div style={{ fontWeight: 500 }}>{child.student_name}</div>
                    <span style={{ color: '#525252', fontSize: '12px' }}>
                      {child.age_group.replace('_', ' ')} • Grade {child.grade_level}
                    </span>
                  </div>
                </Stack>
              }
              extra={
                <Tooltip title="View detailed progress">
                  <Button
                    kind="primary"
                    icon={<RightOutlined />}
                    onClick={() => navigate(`/students/${child.student_id}`)}
                  >
                    Details
                  </Button>
                </Tooltip>
              }
            >
              {/* Progress Metrics Grid */}
              <Grid gutter={[16]} style={{ marginBottom: 16 }}>
                <Column span={8}>
                  <Tile size="small" style={{ textAlign: 'center', background: '#f0f5ff' }}>
                    <Statistic
                      title="Mastery"
                      value={child.mastery_level}
                      suffix="%"
                      valueStyle={{ fontSize: '20px', color: '#1890ff' }}
                    />
                    <ProgressIndicator
                      percent={child.mastery_level}
                      size="small"
                      showInfo={false}
                      strokeColor="#1890ff"
                    />
                  </Tile>
                </Column>
                <Column span={8}>
                  <Tile size="small" style={{ textAlign: 'center', background: '#f6ffed' }}>
                    <Statistic
                      title="Engagement"
                      value={child.engagement_score}
                      suffix="%"
                      valueStyle={{ fontSize: '20px', color: '#52c41a' }}
                    />
                    <ProgressIndicator
                      percent={child.engagement_score}
                      size="small"
                      showInfo={false}
                      strokeColor="#52c41a"
                    />
                  </Tile>
                </Column>
                <Column span={8}>
                  <Tile size="small" style={{ textAlign: 'center', background: '#fff7e6' }}>
                    <Statistic
                      title="Streak"
                      value={child.current_streak}
                      suffix="days"
                      valueStyle={{ fontSize: '20px', color: '#faad14' }}
                      prefix={<FireOutlined />}
                    />
                  </Tile>
                </Column>
              </Grid>

              {/* Activity Summary */}
              <Tile size="small" type="inner" title="This Week's Activity" style={{ marginBottom: 12 }}>
                <Grid gutter={16}>
                  <Column span={8}>
                    <span style={{ color: '#525252' }}>Sessions</span>
                    <div style={{ fontSize: 18, fontWeight: 500 }}>
                      {child.recent_activity_summary.total_sessions}
                      {child.recent_activity_summary.week_over_week_change !== 0 && (
                        <span
                          style={{
                            fontSize: 12,
                            marginLeft: 4,
                            color: child.recent_activity_summary.week_over_week_change > 0 ? '#52c41a' : '#f5222d',
                          }}
                        >
                          {child.recent_activity_summary.week_over_week_change > 0 ? '+' : ''}
                          {child.recent_activity_summary.week_over_week_change}%
                        </span>
                      )}
                    </div>
                  </Column>
                  <Column span={8}>
                    <span style={{ color: '#525252' }}>Time Spent</span>
                    <div style={{ fontSize: 18, fontWeight: 500 }}>
                      {Math.round((child.recent_activity_summary.total_minutes / 60) * 10) / 10} hrs
                    </div>
                  </Column>
                  <Column span={8}>
                    <span style={{ color: '#525252' }}>Favorite</span>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      <Education /> {child.recent_activity_summary.favorite_subject || 'None'}
                    </div>
                  </Column>
                </Grid>
              </Tile>

              {/* Highlights & Concerns */}
              <Stack direction="vertical" style={{ width: '100%' }}>
                {child.progress_highlights.length > 0 && (
                  <InlineNotification
                    message="Progress Highlights"
                    description={
                      <Stack wrap>
                        {child.progress_highlights.map((highlight, index) => (
                          <Tag key={index} color="success" icon={<CheckCircleOutlined />}>
                            {highlight}
                          </Tag>
                        ))}
                      </Stack>
                    }
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                  />
                )}

                {child.areas_of_concern.length > 0 && (
                  <InlineNotification
                    message="Needs Attention"
                    description={
                      <Stack direction="vertical">
                        {child.areas_of_concern.map(concern => (
                          <div key={index}>
                            <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                              {concern.area}
                            </Tag>
                            <span style={{ color: '#525252', fontSize: 12 }}>{concern.recommended_action}</span>
                          </div>
                        ))}
                      </Stack>
                    }
                    type="warning"
                    showIcon
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        ))}
      </Grid>

      {/* Learning Insights Card */}
      {dashboardData.learning_insights && (
        <Tile
          title={
            <Stack>
              <BulbOutlined style={{ color: '#faad14' }} />
              <span>AI-Powered Learning Insights</span>
            </Stack>
          }
          style={{ marginBottom: 32, borderRadius: 8 }}
        >
          <Grid gutter={[24]}>
            <Column xs={24} sm={12} lg={6}>
              <div>
                <span style={{ color: '#525252' }}>Peak Learning Time</span>
                <div style={{ fontSize: 18, fontWeight: 500, marginTop: 4 }}>
                  <Calendar /> {dashboardData.learning_insights.peak_learning_time}
                </div>
              </div>
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <div>
                <span style={{ color: '#525252' }}>Average Session</span>
                <div style={{ fontSize: 18, fontWeight: 500, marginTop: 4 }}>
                  <ClockCircleOutlined /> {dashboardData.learning_insights.average_session_duration} minutes
                </div>
              </div>
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <div>
                <span style={{ color: '#525252' }}>Strongest Areas</span>
                <div style={{ marginTop: 4 }}>
                  {dashboardData.learning_insights.strongest_subjects.map((subject, index) => (
                    <Tag key={index} color="success" style={{ marginBottom: 4 }}>
                      {subject}
                    </Tag>
                  ))}
                </div>
              </div>
            </Column>
            <Column xs={24} sm={12} lg={6}>
              <div>
                <span style={{ color: '#525252' }}>Focus Areas</span>
                <div style={{ marginTop: 4 }}>
                  {dashboardData.learning_insights.improvement_areas.map(area => (
                    <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
                      {area}
                    </Tag>
                  ))}
                </div>
              </div>
            </Column>
          </Grid>
          <InlineNotification
            message="Recommendation"
            description={dashboardData.learning_insights.recommended_focus}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            icon={<BulbOutlined />}
          />
        </Tile>
      )}

      {/* Bottom Section - Achievements & Activities */}
      <Grid gutter={[16]}>
        {/* Achievement Timeline */}
        <Column xs={24} lg={12}>
          <Tile
            title={
              <Stack>
                <Trophy style={{ color: '#faad14' }} />
                <span>Recent Achievements</span>
                <Tag count={dashboardData.family_communication_center.recent_achievements_to_celebrate.length} />
              </Stack>
            }
            extra={<Button type="link">View All</Button>}
            style={{ borderRadius: 8 }}
          >
            {dashboardData.family_communication_center.recent_achievements_to_celebrate.length > 0 ? (
              <Timeline
                items={dashboardData.family_communication_center.recent_achievements_to_celebrate.map(achievement => ({
                  dot: (
                    <Trophy
                      style={{
                        fontSize: '16px',
                        color:
                          achievement.significance_level === 'exceptional'
                            ? '#faad14'
                            : achievement.significance_level === 'major'
                              ? '#1890ff'
                              : '#52c41a',
                      }}
                    />
                  ),
                  children: (
                    <div>
                      <Stack>
                        <Tag
                          color={
                            achievement.significance_level === 'exceptional'
                              ? 'gold'
                              : achievement.significance_level === 'major'
                                ? 'blue'
                                : 'green'
                          }
                        >
                          {achievement.significance_level}
                        </Tag>
                        <span style={{ color: '#525252', fontSize: 12 }}>
                          {new Date(achievement.achieved_date).toLocaleDateString()}
                        </span>
                      </Stack>
                      <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                        <span style={{ fontWeight: 'bold' }}>{achievement.student_name}:</span>{' '}
                        {achievement.milestone_description}
                      </Paragraph>
                    </div>
                  ),
                }))}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '24px' }}>No data available</div>
            )}
          </Tile>
        </Column>

        {/* Family Activities */}
        <Column xs={24} lg={12}>
          <Tile
            title={
              <Stack>
                <UserMultiple style={{ color: '#52c41a' }} />
                <span>Family Learning Activities</span>
              </Stack>
            }
            extra={<Button type="link">Browse All</Button>}
            style={{ borderRadius: 8 }}
          >
            <UnorderedList
              dataSource={dashboardData.collaborative_learning_opportunities}
              renderItem={activity => (
                <ListItem
                  actions={[
                    <Button kind="primary" size="small">
                      Start
                    </Button>,
                  ]}
                >
                  <div /* ListItem.Meta */
                    avatar={activity.difficulty_level?.[0] || 'B'}
                    title={
                      <Stack>
                        {activity.activity_title}
                        <Tag color="blue">{activity.estimated_duration}</Tag>
                      </Stack>
                    }
                    description={
                      <div>
                        <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 4 }}>
                          {activity.educational_value}
                        </Paragraph>
                        <Stack>
                          <Tag>{activity.age_appropriateness}</Tag>
                          {activity.materials_needed.map((material, idx) => (
                            <Tag key={idx} color="default">
                              {material}
                            </Tag>
                          ))}
                        </Stack>
                      </div>
                    }
                  />
                </ListItem>
              )}
            />
          </Tile>
        </Column>
      </Grid>
    </div>
  );
}

export default ParentDashboardEnhanced;
